import { Modal, PrimaryButton, toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { ErrorResponseContentInterface } from '@systemeio/util'
import FormSenderEmailInput from 'modules/email/components/form-sender-email-input'
import { useSendTestEmail } from 'modules/mailing/hooks/use-send-test-email'
import { MailingId } from 'modules/mailing/types/mailing-interface'
import { useMailingSettings } from 'modules/profile/mailing-settings/hooks/use-mailing-settings'
import React, { useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { HTMLString } from 'shared/types/html-string'

export interface SendTestEmailModalProps {
  mailingId: MailingId
  onSave: () => Promise<void>
  onClose: () => void
  opened: boolean
}

export const SendTestEmailModal = ({
  mailingId,
  onSave,
  onClose,
  opened,
}: SendTestEmailModalProps) => {
  const { t } = useLocoTranslation()

  const [email, setEmail] = useState<string>('')
  const [hasEmailBeenChanged, setHasEmailBeenChanged] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [isFetching, setIsFetching] = useState(false)

  const { fetcherSendTestEmail } = useSendTestEmail()
  const { mailingData } = useMailingSettings()

  const handelClose = () => {
    onClose()
    setError(null)
  }

  const handleConfirm = async () => {
    setIsFetching(true)
    try {
      await onSave()
      await fetcherSendTestEmail(mailingId, {
        email,
      })
      onClose()
    } catch (e) {
      if (e instanceof BadRequest) {
        const errors = e.errors as ErrorResponseContentInterface<{ email: HTMLString[] }>
        if (errors?.fields?.email) {
          setError(errors.fields.email.join(''))
        }
        if (errors.common?.length) {
          toast.error(errors.common.join(''))
        }
      }
    }
    setIsFetching(false)
  }

  useEffect(() => {
    if (!hasEmailBeenChanged && !email && mailingData) {
      setEmail(mailingData.testEmailAddress)
    }
  }, [email, hasEmailBeenChanged, mailingData])

  return (
    <Modal
      isFetching={isFetching}
      isCentered
      opened={opened}
      onClose={handelClose}
      title={t('dashboard.email.send_test_modal.title')}
    >
      <form className="m-auto flex w-full flex-col gap-6 lg:gap-10">
        <FormSenderEmailInput
          value={email || ''}
          className="w-full"
          key={'email'}
          isPreFetching={!mailingData}
          required
          onChange={e => {
            setHasEmailBeenChanged(true)
            setEmail(e.target.value)
          }}
          htmlFor={'none'}
          label={t('dashboard.email.test_email_address')}
          error={error}
        />
        <div className="flex justify-center">
          <PrimaryButton
            isFetching={isFetching}
            disabled={!mailingData}
            width="large"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              handleConfirm()
            }}
            type="submit"
          >
            {t('global.confirm')}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  )
}
